import { z } from 'zod';
import { propertySearchSchema, type PropertySearch } from './catalog';
import {
  sourceFactSchema,
  VIRTUAL_ASSISTANT_DISCLOSURE,
  type AssistantConfiguration,
  type BusinessAction,
  type SourceFact,
} from './assistant';
import { type PropertyCatalogResult } from './catalog-results';
import { formatExactMoney } from './money';
import { uuidV7Schema } from './ids';
import { knownCatalogRead, matchesCatalogReference } from './catalog-engine';
import type { CatalogConfiguration } from './catalog-contracts';

const common = {
  title: z.string().trim().min(1).max(200),
  examples: z.array(z.string().trim().min(1).max(300)).min(1).max(8),
  locale: z.enum(['es-PE', 'en-US']),
};
export const memoryPlanSchema = z.discriminatedUnion('kind', [
  z
    .object({
      ...common,
      kind: z.literal('knowledge'),
      answer: z.string().trim().min(1).max(3000),
      sourceIds: z.array(uuidV7Schema).min(1).max(6),
    })
    .strict(),
  z
    .object({
      ...common,
      kind: z.literal('catalog'),
      query: z.string().trim().min(1).max(500),
      propertySearch: propertySearchSchema.nullable(),
      binding: z.enum(['fixed', 'unit_code', 'max_price_pen', 'max_price_usd']),
    })
    .strict(),
]);
export type MemoryPlan = z.infer<typeof memoryPlanSchema>;
export interface MemoryCandidate {
  id: string;
  plan: MemoryPlan;
  action: BusinessAction | null;
}
export const memorySettingsSchema = z
  .object({
    enabled: z.boolean(),
    learningEnabled: z.boolean(),
    dailyLearningLimit: z.number().int().min(0).max(100),
  })
  .strict();
export const DEFAULT_MEMORY_SETTINGS = {
  enabled: true,
  learningEnabled: true,
  dailyLearningLimit: 10,
} as const;
export const MEMORY_ENGINE_VERSION = 3;

export function memorySearchText(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

// Only unambiguous, explicit numeric slots are resolved here. Other language goes to Luna.
export function bindMemoryPlan(
  plan: MemoryPlan,
  text: string,
  catalog?: CatalogConfiguration
): BusinessAction | null {
  if (plan.kind !== 'catalog') return null;
  let search = plan.propertySearch;
  if (plan.binding !== 'fixed') {
    if (!search || search.alternatives.length !== 1) return null;
    const normalized = text.normalize('NFKC').toLowerCase();
    const numbers = normalized.match(/\d+(?:[.,]\d+)*/gu) ?? [];
    if (numbers.length !== 1) return null;
    const alternative = { ...search.alternatives[0]! };
    if (plan.binding === 'unit_code') {
      const code = knownCatalogRead(text, catalog)?.propertySearch
        ?.alternatives[0]?.unitCode;
      if (!code) return null;
      alternative.unitCode = code.toUpperCase();
      alternative.unitId = null;
    } else {
      const pen = /(?:s\/\.?|\bsoles?\b|\bpen\b)/iu.test(normalized);
      const usd = /(?:us\$|\busd\b|\bd[oó]lares?\b|\bdollars?\b)/iu.test(
        normalized
      );
      if (pen === usd || pen !== (plan.binding === 'max_price_pen'))
        return null;
      const number = numbers[0]!;
      // Decimal/grouping ambiguity deliberately has no fast-path interpretation.
      if (!/^\d+$/u.test(number)) return null;
      if (
        /(?:no menos|not less|at least|como m[ií]nimo|[+-]\s*\d)/iu.test(
          normalized
        )
      )
        return null;
      const strict =
        /(?:menos (?:de|que)|debajo de|\bunder\b|\bbelow\b|less than)/iu.test(
          normalized
        );
      const inclusive =
        /(?:\bhasta\b|\bmaxim[oa]\b|\bmáxim[oa]\b|no m[aá]s de|up to|at most|maximum)/iu.test(
          normalized
        );
      if (strict === inclusive) return null;
      const thousands = /\b(?:mil|thousand)\b/iu.test(normalized);
      const amount = (
        BigInt(number) * (thousands ? 100000n : 100n) -
        (strict ? 1n : 0n)
      ).toString();
      if (amount.length > 18 || BigInt(amount) <= 0n) return null;
      if (pen) alternative.maxPricePenMinor = amount;
      else alternative.maxPriceUsdMinor = amount;
    }
    search = propertySearchSchema.parse({
      ...search,
      alternatives: [alternative],
    });
  }
  return {
    kind: 'search_offerings',
    query: plan.query,
    propertySearch: search,
  };
}

// Candidate construction copies only typed public criteria, never query prose or customer history.
export function catalogMemoryPlan(
  search: PropertySearch,
  locale: 'es-PE' | 'en-US',
  catalog: PropertyCatalogResult
): Extract<MemoryPlan, { kind: 'catalog' }> | null {
  if (
    search.unsupportedCriteria.length ||
    catalog.unverifiedCriteria?.length ||
    search.alternatives.some((a) => a.cursor || a.unitId)
  )
    return null;
  const safe = propertySearchSchema.parse(search);
  for (const alternative of safe.alternatives) {
    if (
      alternative.unitCode &&
      !catalog.items.some((item) =>
        matchesCatalogReference(item, {
          kind: 'code',
          value: alternative.unitCode!,
        })
      )
    )
      return null;
  }
  const first = safe.alternatives[0]!;
  let binding: 'fixed' | 'unit_code' | 'max_price_pen' | 'max_price_usd' =
    'fixed';
  if (safe.alternatives.length === 1) {
    if (first.unitCode) binding = 'unit_code';
    else if (first.maxPricePenMinor && !first.maxPriceUsdMinor)
      binding = 'max_price_pen';
    else if (first.maxPriceUsdMinor && !first.maxPricePenMinor)
      binding = 'max_price_usd';
  }
  if (binding === 'unit_code') first.unitCode = null;
  if (binding === 'max_price_pen') first.maxPricePenMinor = null;
  if (binding === 'max_price_usd') first.maxPriceUsdMinor = null;
  const features = [
    ...new Set(safe.alternatives.flatMap((a) => a.amenities ?? [])),
  ].join(', ');
  const examples =
    binding === 'unit_code'
      ? [
          'Precio, área y disponibilidad del departamento {{unit_code}}',
          'Price, area and availability of apartment {{unit_code}}',
        ]
      : binding.startsWith('max_price')
        ? [
            `Departamentos hasta {{${binding}}} ${binding === 'max_price_pen' ? 'soles' : 'dólares'}`,
            `Apartments up to {{${binding}}} ${binding === 'max_price_pen' ? 'PEN' : 'USD'}`,
          ]
        : [
            'Buscar departamentos disponibles ' + features,
            'Find available apartments ' + features,
          ];
  return memoryPlanSchema.options[1].parse({
    kind: 'catalog',
    title: examples[locale === 'es-PE' ? 0 : 1],
    examples,
    locale,
    query: examples[locale === 'es-PE' ? 0 : 1],
    propertySearch: safe,
    binding,
  });
}

export function renderMemoryCatalog(
  catalog: PropertyCatalogResult,
  locale: 'es-PE' | 'en-US'
): string {
  const es = locale === 'es-PE';
  const rows = catalog.items.map((item) => {
    const prices = [
      item.pricePenMinor === null
        ? null
        : formatExactMoney(BigInt(item.pricePenMinor), 'PEN', 2, locale),
      item.priceUsdMinor === null
        ? null
        : formatExactMoney(BigInt(item.priceUsdMinor), 'USD', 2, locale),
    ].filter(Boolean);
    const fields = [
      prices.join(' / ') ||
        (es ? 'precio no publicado' : 'price not published'),
      item.totalAreaM2 === null
        ? es
          ? 'área no informada'
          : 'area not reported'
        : `${item.totalAreaM2} m²`,
    ];
    if (item.bedroomsCount != null)
      fields.push(
        `${item.bedroomsCount} ${es ? (item.bedroomsCount === 1 ? 'dormitorio' : 'dormitorios') : item.bedroomsCount === 1 ? 'bedroom' : 'bedrooms'}`
      );
    if (item.bathroomsCount != null)
      fields.push(
        `${item.bathroomsCount} ${es ? (item.bathroomsCount === 1 ? 'baño' : 'baños') : item.bathroomsCount === 1 ? 'bathroom' : 'bathrooms'}`
      );
    if (item.floorNumber != null)
      fields.push(`${es ? 'piso' : 'floor'} ${item.floorNumber}`);
    return `• ${item.code}: ${fields.join(' · ')}.`;
  });
  if (!rows.length)
    return es
      ? 'El catálogo actual no encontró unidades que coincidan con estos criterios. ¿Quieres cambiar algún filtro?'
      : 'The current catalog found no units matching these criteria. Would you like to change a filter?';
  const intro =
    rows.length === 1
      ? es
        ? 'Esta opción figura disponible:'
        : 'This option is listed as available:'
      : es
        ? `Encontré ${rows.length} opciones que figuran disponibles:`
        : `I found ${rows.length} options listed as available:`;
  const caution = es
    ? 'Los precios y la disponibilidad pueden cambiar.'
    : 'Prices and availability can change.';
  return [
    intro,
    ...rows,
    catalog.hasMore
      ? es
        ? 'Hay más resultados fuera de esta página.'
        : 'More results exist beyond this page.'
      : '',
    caution,
    es
      ? '¿Quieres revisar alguna opción con más detalle?'
      : 'Would you like to explore an option in more detail?',
  ]
    .filter(Boolean)
    .join('\n\n');
}

export const memoryLearningSchema = z.discriminatedUnion('kind', [
  z
    .object({ kind: z.literal('catalog'), plan: memoryPlanSchema.options[1] })
    .strict(),
  z
    .object({
      kind: z.literal('general_catalog'),
      facts: z.array(sourceFactSchema).min(1).max(6),
      locale: z.enum(['es-PE', 'en-US']),
    })
    .strict(),
  z
    .object({
      kind: z.literal('knowledge'),
      sourceIds: z.array(uuidV7Schema).min(1).max(6),
      locale: z.enum(['es-PE', 'en-US']),
    })
    .strict(),
]);
export type MemoryLearning = z.infer<typeof memoryLearningSchema>;
export function memoryPublicFacts(
  facts: readonly SourceFact[],
  ids: readonly string[]
): string[] {
  return facts
    .filter(
      (f) =>
        !f.private && ids.includes(f.id) && uuidV7Schema.safeParse(f.id).success
    )
    .map((f) => f.id)
    .slice(0, 6);
}

export function memoryReplyText(
  text: string,
  channel: 'chat' | 'email',
  profile: AssistantConfiguration['profile'],
  locale: 'es-PE' | 'en-US'
): string {
  if (channel === 'chat') return text;
  return [
    text,
    `${profile.assistantName} · ${VIRTUAL_ASSISTANT_DISCLOSURE[locale]}`,
    profile.businessName,
    profile.signature,
  ]
    .filter(Boolean)
    .join('\n\n');
}
